import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BrainCircuit,
  Activity,
  CheckCircle,
  AlertTriangle,
  Clock,
  Loader2,
  ChevronDown,
  ChevronUp,
  Info,
  Zap,
  Database,
} from 'lucide-react'
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import { cn } from '@/lib/utils'
import { ML_MODELS } from '@/lib/mockData'
import { formatDate } from '@/lib/utils'
import type { ModelInfo, RiskCategory } from '@/types'

// Status badge – uses lowercase ModelStatus ('active' | 'training' | 'inactive' | 'error')
function StatusBadge({ status }: { status: ModelInfo['status'] }) {
  const map: Record<string, { color: string; icon: React.ComponentType<{ size?: number | string; className?: string }>; label: string }> = {
    active: { color: 'bg-terra-green/20 text-terra-green border-terra-green/30', icon: CheckCircle, label: 'Attivo' },
    training: { color: 'bg-terra-yellow/20 text-terra-yellow border-terra-yellow/30', icon: Loader2, label: 'Training' },
    error: { color: 'bg-terra-red/20 text-terra-red border-terra-red/30', icon: AlertTriangle, label: 'Errore' },
    inactive: { color: 'bg-slate-700/60 text-slate-400 border-slate-600/60', icon: Clock, label: 'Inattivo' },
  }
  const config = map[status] ?? map['inactive']
  const { color, icon: Icon, label } = config
  return (
    <span className={cn('flex items-center gap-1 border text-xs font-medium px-2 py-0.5 rounded', color)}>
      <Icon size={10} className={status === 'training' ? 'animate-spin' : ''} />
      {label}
    </span>
  )
}

function CategoryBadge({ category }: { category: RiskCategory }) {
  const map: Record<RiskCategory, { color: string; label: string }> = {
    landslide: { color: 'bg-terra-orange/20 text-terra-orange border-terra-orange/30', label: 'Frana' },
    flood: { color: 'bg-terra-blue/20 text-terra-blue border-terra-blue/30', label: 'Alluvione' },
    erosion: { color: 'bg-terra-purple/20 text-terra-purple border-terra-purple/30', label: 'InSAR/Erosione' },
    combined: { color: 'bg-terra-cyan/20 text-terra-cyan border-terra-cyan/30', label: 'Ibrido' },
  }
  const { color, label } = map[category]
  return <span className={cn('border text-xs font-medium px-2 py-0.5 rounded', color)}>{label}</span>
}

function MetricBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-xs text-slate-500">{label}</span>
        <span className="text-xs font-bold" style={{ color }}>{(value * 100).toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${value * 100}%` }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          className="h-full rounded-full"
          style={{ background: color }}
        />
      </div>
    </div>
  )
}

function ModelCard({ model, index }: { model: ModelInfo; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const auc = model.auc_roc ?? 0
  const prec = model.precision ?? 0
  const rec = model.recall ?? 0
  const f1 = model.f1_score ?? 0
  const radarData = [
    { metric: 'AUC-ROC', value: auc },
    { metric: 'Precision', value: prec },
    { metric: 'Recall', value: rec },
    { metric: 'F1', value: f1 },
  ]
  const categoryColor: Record<RiskCategory, string> = {
    landslide: '#f97316',
    flood: '#0ea5e9',
    erosion: '#8b5cf6',
    combined: '#06b6d4',
  }
  const typeColor = categoryColor[model.category]

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.35 }}
      className={cn('terra-card transition-all duration-300', expanded && 'border-terra-blue/30')}
    >
      <button className="w-full text-left" onClick={() => setExpanded((e) => !e)}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: typeColor + '18', border: `1px solid ${typeColor}35` }}>
            <BrainCircuit size={18} style={{ color: typeColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-white">{model.name}</h3>
              <span className="text-xs text-slate-600 font-mono">v{model.version}</span>
              <CategoryBadge category={model.category} />
              <StatusBadge status={model.status} />
            </div>
            <p className="text-xs text-slate-400 mt-1 line-clamp-1">{model.description}</p>
          </div>
          <div className="flex-shrink-0 text-right hidden sm:block">
            <div className="text-xl font-bold" style={{ color: typeColor }}>{(auc * 100).toFixed(1)}%</div>
            <div className="text-xs text-slate-500">AUC-ROC</div>
          </div>
          <div className="flex-shrink-0 text-slate-500 ml-2">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>
        <div className="flex items-center gap-6 mt-3 pl-14">
          {[{ label: 'Precision', value: prec }, { label: 'Recall', value: rec }, { label: 'F1', value: f1 }].map(({ label, value }) => (
            <div key={label} className="text-center">
              <div className="text-xs font-bold text-white">{(value * 100).toFixed(1)}%</div>
              <div className="text-xs text-slate-600">{label}</div>
            </div>
          ))}
          {model.last_inference && (
            <div className="ml-auto flex items-center gap-1 text-xs text-slate-600">
              <Clock size={10} />
              {formatDate(model.last_inference)}
            </div>
          )}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="terra-divider" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              <div>
                <p className="text-xs font-semibold text-slate-300 mb-3">Metriche di Performance</p>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="rgba(51,65,85,0.5)" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: '#64748b', fontSize: 11 }} />
                    <Radar dataKey="value" stroke={typeColor} fill={typeColor} fillOpacity={0.2} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                <div className="space-y-3">
                  <MetricBar label="AUC-ROC" value={auc} color={typeColor} />
                  <MetricBar label="Precision" value={prec} color={typeColor} />
                  <MetricBar label="Recall" value={rec} color={typeColor} />
                  <MetricBar label="F1 Score" value={f1} color={typeColor} />
                </div>
                <div className="terra-divider" />
                <div className="space-y-2">
                  {model.training_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 flex items-center gap-1.5"><Database size={11} /> Training</span>
                      <span className="text-xs text-white font-mono">{model.training_date}</span>
                    </div>
                  )}
                  {model.last_inference && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 flex items-center gap-1.5"><Activity size={11} /> Ultima inferenza</span>
                      <span className="text-xs text-white font-mono">{formatDate(model.last_inference)}</span>
                    </div>
                  )}
                  {model.inference_count_24h !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 flex items-center gap-1.5"><Zap size={11} /> Inferenze 24h</span>
                      <span className="text-xs text-white font-mono">{model.inference_count_24h.toLocaleString('it-IT')}</span>
                    </div>
                  )}
                </div>
                {model.data_sources.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {model.data_sources.map((src) => (
                      <span key={src} className="text-xs bg-slate-800/60 text-slate-400 border border-slate-700/60 rounded px-1.5 py-0.5 font-mono uppercase">{src}</span>
                    ))}
                  </div>
                )}
                <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-800/60">
                  <div className="flex items-start gap-2">
                    <Info size={12} className="text-slate-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-slate-400">{model.description}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function ModelComparisonChart() {
  const data = ML_MODELS.map((m) => ({
    name: m.name.split(' ')[0],
    'AUC-ROC': parseFloat(((m.auc_roc ?? 0) * 100).toFixed(1)),
    'F1 Score': parseFloat(((m.f1_score ?? 0) * 100).toFixed(1)),
  }))

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="terra-card">
      <div className="flex items-center gap-2 mb-4">
        <Zap size={14} className="text-terra-yellow" />
        <h3 className="text-sm font-semibold text-white">Confronto Performance Modelli</h3>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -15 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.4)" />
          <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
          <YAxis domain={[80, 100]} tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: '8px', fontSize: '12px', color: '#e2e8f0' }}
            formatter={(val: number) => [`${val}%`]}
          />
          <Bar dataKey="AUC-ROC" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
          <Bar dataKey="F1 Score" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </motion.div>
  )
}

export default function MLModelsPage() {
  const activeCount = ML_MODELS.filter((m) => m.status === 'active').length
  const trainingCount = ML_MODELS.filter((m) => m.status === 'training').length

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Modelli ML</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              {ML_MODELS.length} modelli registrati · {activeCount} attivi
              {trainingCount > 0 && ` · ${trainingCount} in training`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-terra-green">
                <div className="w-1.5 h-1.5 rounded-full bg-terra-green animate-pulse" />
                {activeCount} attivi
              </div>
            )}
            {trainingCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-terra-yellow">
                <Loader2 size={12} className="animate-spin" />
                {trainingCount} in training
              </div>
            )}
          </div>
        </div>
      </motion.div>

      <ModelComparisonChart />

      <div className="space-y-4">
        {ML_MODELS.map((model, i) => (
          <ModelCard key={model.id} model={model} index={i} />
        ))}
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="glass rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-400">
            I modelli sono valutati su dataset di validazione indipendenti comprendenti eventi storici IFFI (1954-2024)
            e retrocast Emilia-Romagna 2023 / Niscemi 2026. Le metriche riportate si riferiscono all'insieme di test
            (hold-out 20%). Per dettagli metodologici consultare la documentazione tecnica su Confluence (pagina 04).
          </p>
        </div>
      </motion.div>
    </div>
  )
}
